import { useAppKitNetwork } from "@reown/appkit/react";
import { simulateContract, waitForTransactionReceipt } from "@wagmi/core";
import { useCallback, useState } from "react";
import { useConfig, useWriteContract } from "wagmi";

import { useDailySpinOnchain } from "./useDailySpinOnchain";

import { dailySpinChain, dailySpinChainId } from "@/constants/shared";
import { DAILY_SPIN_ABI } from "@/network/abi/daily-spin";
import { dailySpinToaster } from "@/pages/DailySpin/constants";

type ConversionResult = {
  ticketsConverted: number;
  ppGained: number;
};

export const useConvertRaffleTicketsToPP = () => {
  const wagmiConfig = useConfig();
  const [isConverting, setIsConverting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [conversionResult, setConversionResult] =
    useState<ConversionResult | null>(null);
  const { switchNetwork } = useAppKitNetwork();
  const { writeContractAsync } = useWriteContract();
  const { refetchDailySpinStats, raffleTicketsBalance } = useDailySpinOnchain();

  const convert = useCallback(async () => {
    if (raffleTicketsBalance <= 0) {
      dailySpinToaster.create({
        type: "error",
        title: "No tickets to convert",
        description: "You don't have any raffle tickets to convert.",
      });
      return;
    }

    setIsConverting(true);
    setIsSuccess(false);
    setConversionResult(null);

    // Store the current balance before conversion for the success modal
    const ticketsToConvert = raffleTicketsBalance;
    const expectedPP = Math.ceil(ticketsToConvert / 10);

    try {
      await switchNetwork(dailySpinChain);

      // Simulate the contract call first
      await simulateContract(wagmiConfig, {
        abi: DAILY_SPIN_ABI,
        address: import.meta.env.VITE_DAILY_SPIN_CONTRACT_ADDRESS,
        functionName: "convertRaffleTicketsToPP",
        chainId: dailySpinChainId,
      });

      // Execute the transaction
      const hash = await writeContractAsync({
        abi: DAILY_SPIN_ABI,
        address: import.meta.env.VITE_DAILY_SPIN_CONTRACT_ADDRESS,
        functionName: "convertRaffleTicketsToPP",
        chainId: dailySpinChainId,
      });

      // Wait for transaction confirmation
      await waitForTransactionReceipt(wagmiConfig, { hash });

      setIsSuccess(true);
      setConversionResult({
        ticketsConverted: ticketsToConvert,
        ppGained: expectedPP,
      });

      // Refetch to update the UI
      refetchDailySpinStats();
    } catch (error) {
      console.error(
        "[ConvertRaffleTicketsToPP] Error converting tickets:",
        error,
      );
      dailySpinToaster.create({
        type: "error",
        title: "Conversion failed",
        description: "Failed to convert tickets. Please try again.",
      });
    } finally {
      setIsConverting(false);
    }
  }, [
    switchNetwork,
    wagmiConfig,
    writeContractAsync,
    refetchDailySpinStats,
    raffleTicketsBalance,
  ]);

  const resetSuccess = useCallback(() => {
    setIsSuccess(false);
    setConversionResult(null);
  }, []);

  return {
    convert,
    isConverting,
    isSuccess,
    conversionResult,
    resetSuccess,
    ticketsToConvert: raffleTicketsBalance,
    expectedPP:
      raffleTicketsBalance > 0 ? Math.ceil(raffleTicketsBalance / 10) : 0,
  };
};
